import {
  alpha,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled } from "@reduxjs/toolkit";
import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CustomerOrgCompanyAutocompleteSingle from "../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/CustomerOrgCompanyAutocompleteSingle";
import useCustomerOrgCompanyAutocomplete from "../../../common/components/functional/autocomplete/customerOrgCompanyAutocomplete/useCustomerOrgCompanyAutocomplete";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import { WorkLocationAutocompleteFieldMulti } from "../../../common/components/functional/fields/workLocationAutocompleteField/WorkLocationAutocompleteFieldMulti";
import WorkTitleAutocompleteField from "../../../common/components/functional/fields/WorkTitleAutocompleteField";
import ZonedSalaryValue from "../../../common/components/functional/ZonedSalaryValue";
import { FormSectionHeader } from "../../../common/components/styled/styled";
import {
  isoFormat,
  leadingZerosDateFormat,
  validateDateInput,
} from "../../../common/helpers/formatDateTime";
import isEmpty from "../../../common/helpers/isEmpty";
import { ReactComponent as DeleteIcon } from "../../../common/icons/delete.svg";
import { NameIdOption } from "../../../common/types/commonTypes";
import {
  buildZonedSalaryComponents,
  formatSalaryComponents,
} from "../../../common/utils/formatSalaryComponents";
import {
  calculateTotalSalary,
  calculateZonedTotalSalary,
} from "../../../common/utils/salaryUtils";
import SalaryComponents from "../../talentProfile/editForms/salaries/SalaryComponents";
import { TargetedShiftInfoText } from "../../talentProfile/Employment/EmploymentAdditionalInfo/targetedShifts/styles";
import { fetchEmploymentAttributesForEditing } from "../employmentSlice";
import {
  findAmbiguousOverlap,
  getExistingSalary,
  isDuplicateKeys,
  mergeSalaryRange,
  removeSalaryRange,
  toCommandKeys,
} from "./helpers";
import {
  removeTargetedShiftSalaryRow,
  saveTargetedShiftSalaryRow,
} from "./targetedShiftSalariesActions";
import {
  DateBasedSalary,
  SalaryDateSpec,
  TargetedShiftSpec,
  TargetingKeys,
} from "./targetedShiftSalariesApi";
import { TargetedShiftSalaryFormValues } from "./types";

interface TargetedShiftSalaryDialogBaseProps {
  open: boolean;
  tesId: string;
  /**
   * The talent whose employment attributes are used to determine whether
   * the TES supports zoned (e.g. PKS) salaries. Reused from
   * `employmentAttributesForEditing` even though this dialog does not edit
   * that talent's regular employment attributes.
   */
  talentId: string;
  workTitleId: string;
  spec: TargetedShiftSpec;
  canEdit: boolean;
  onClose: () => void;
  onSaved: (updatedSpec: TargetedShiftSpec) => void;
  editingKey?: TargetingKeys | null;
  contractId?: string;
  /**
   * The customer company the targeting rules are tied to. When provided,
   * only organizations belonging to it can be selected for the salary rule.
   */
  customerCompany?: NameIdOption;
  minDate?: Date | null;
  maxDate?: Date | null;
}

interface EmploymentModeProps extends TargetedShiftSalaryDialogBaseProps {
  mode: "employment";
  employmentId: string;
}

interface LocalModeProps extends TargetedShiftSalaryDialogBaseProps {
  mode: "local";
}

type Props = EmploymentModeProps | LocalModeProps;

export const TargetedShiftSalaryDialog = (props: Props) => {
  const {
    open,
    tesId,
    talentId,
    workTitleId,
    spec,
    canEdit,
    onClose,
    onSaved,
    editingKey,
    contractId,
    customerCompany,
    minDate,
    maxDate,
  } = props;
  const { t } = useTranslation(["talentProfile", "common"]);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRangeDate, setSelectedRangeDate] = useState<string | null>(
    null,
  );

  const isAddMode = editingKey == null;

  const tesTranslations = useAppSelector((state) => state.localized.tes);
  const workTitleTranslations = useAppSelector(
    (state) => state.localized.workTitle,
  );

  // Reused only to resolve which salary zones (e.g. PKS) the TES supports -
  // that part of the response depends solely on tesId, not on the talent.
  const employmentAttributesForEditing = useAppSelector(
    (state) => state.employment.attributesForEditing,
  );

  useEffect(() => {
    if (open && talentId && workTitleId && tesId) {
      dispatch(
        fetchEmploymentAttributesForEditing({ talentId, workTitleId, tesId }),
      );
    }
  }, [open, talentId, workTitleId, tesId, dispatch]);

  const salaryZones =
    employmentAttributesForEditing.attributes?.tesId === tesId
      ? employmentAttributesForEditing.attributes?.salaryZones
      : undefined;

  const {
    customerOptions,
    onInputChange: onCustomerInputChange,
    isLoading: isCustomerLoading,
    isFixedOptions,
  } = useCustomerOrgCompanyAutocomplete({ customerCompany });

  const methods = useForm<TargetedShiftSalaryFormValues>({
    mode: "onChange",
    defaultValues: {
      custCompanyId: null,
      custOrg: null,
      workLocs: [],
      workTitles: [],
      validFrom: null,
      components: {},
      totalSalary: "",
      zonedComponents: {},
    },
  });

  const watchedValidFrom = methods.watch("validFrom");
  const watchedCustOrg = methods.watch("custOrg");
  const watchedWorkLocs = methods.watch("workLocs");
  const watchedWorkTitles = methods.watch("workTitles");

  const isWithinBounds = useCallback(
    (date: Date): boolean => {
      const iso = format(date, isoFormat);
      if (minDate && iso < format(minDate, isoFormat)) return false;
      if (maxDate && iso > format(maxDate, isoFormat)) return false;
      return true;
    },
    [minDate, maxDate],
  );

  const existingSalary = useMemo(
    () => (editingKey ? getExistingSalary(spec, editingKey) : undefined),
    [spec, editingKey],
  );

  useEffect(() => {
    if (!open) {
      methods.reset();
      setSelectedRangeDate(null);
      return;
    }
    if (!isAddMode && editingKey) {
      methods.reset({
        custCompanyId: editingKey.custCompany.id,
        custOrg: editingKey.custOrgs[0] ?? null,
        workLocs: editingKey.workLocs,
        workTitles: editingKey.workTitles.map((id) => ({
          id,
          name: "",
          fieldOfWork: "",
        })),
        validFrom: null,
        components: {},
        totalSalary: "",
        zonedComponents: {},
      });
      setSelectedRangeDate(null);
    } else if (isAddMode) {
      methods.reset({
        custCompanyId: customerCompany?.id ?? null,
        custOrg: null,
        workLocs: [],
        workTitles: [],
        validFrom: null,
        components: {},
        totalSalary: "",
        zonedComponents: {},
      });
      setSelectedRangeDate(null);
    }
  }, [open, isAddMode, editingKey, methods, customerCompany]);

  useEffect(() => {
    if (selectedRangeDate && existingSalary) {
      const range = existingSalary.ranges.find(
        (r) => r.from === selectedRangeDate,
      );
      if (range) {
        methods.setValue("validFrom", parseISO(range.from));
        methods.setValue("components", range.components ?? {}, {
          shouldDirty: true,
        });
        methods.setValue("totalSalary", range.components?.BASE ?? "");
        methods.setValue("zonedComponents", range.zonedComponents ?? {}, {
          shouldDirty: true,
        });
      }
    }
  }, [selectedRangeDate, existingSalary, methods]);

  const buildCommandKeys = useCallback(() => {
    const values = methods.getValues();
    return {
      custCompanyId: values.custCompanyId ?? "",
      custOrgs: values.custOrg ? [values.custOrg.id] : [],
      workLocs: [...values.workLocs.map((wl) => wl.id)].sort(),
      workTitles: [...(values.workTitles ?? []).map((wt) => wt.id)].sort(),
    };
  }, [methods]);

  const buildOptimisticKeys = useCallback((): TargetingKeys => {
    const values = methods.getValues();
    return {
      custCompany: { id: values.custCompanyId ?? "", name: "" },
      custOrgs: values.custOrg ? [values.custOrg] : [],
      workLocs: [...values.workLocs],
      workTitles: [...(values.workTitles ?? []).map((wt) => wt.id)].sort(),
    };
  }, [methods]);

  // Only add mode can introduce a new conflicting scope - edit mode keeps the
  // targeting keys read-only and only changes salary history for that row.
  const conflictingRow = useMemo(
    () =>
      isAddMode
        ? findAmbiguousOverlap(buildOptimisticKeys(), spec.salaries)
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAddMode,
      watchedCustOrg,
      watchedWorkLocs,
      watchedWorkTitles,
      spec.salaries,
      buildOptimisticKeys,
    ],
  );

  const handleSaveRange = useCallback(
    async (data: FieldValues) => {
      const validFrom = data.validFrom as Date | null;
      if (!validFrom || !validateDateInput(validFrom)) return;
      if (isAddMode && !isWithinBounds(validFrom)) return;
      if (isAddMode && conflictingRow) return;

      const apiKeys = isAddMode ? buildOptimisticKeys() : editingKey!;

      const fromDate = format(validFrom, isoFormat);
      const formattedComponents = formatSalaryComponents(
        data.components as Record<string, string>,
      );

      const formattedZonedComponents = buildZonedSalaryComponents(
        data.zonedComponents,
      );

      const newSpec: SalaryDateSpec = {
        from: fromDate,
        components: formattedComponents,
        ...(formattedZonedComponents && {
          zonedComponents: formattedZonedComponents,
        }),
      };

      const currentSalary = getExistingSalary(spec, apiKeys);

      const updatedSalary: DateBasedSalary = currentSalary
        ? mergeSalaryRange(currentSalary, newSpec)
        : { ranges: [newSpec] };

      const exists = spec.salaries.some((r) =>
        isDuplicateKeys(r.keys, apiKeys),
      );
      const updatedSalaries = exists
        ? spec.salaries.map((r) =>
            isDuplicateKeys(r.keys, apiKeys)
              ? { keys: apiKeys, salary: updatedSalary }
              : r,
          )
        : [...spec.salaries, { keys: apiKeys, salary: updatedSalary }];

      if (props.mode === "local") {
        onSaved({ ...spec, salaries: updatedSalaries });
        methods.reset();
        setSelectedRangeDate(null);
        onClose();
        return;
      }

      const cmdKeys = isAddMode
        ? buildCommandKeys()
        : toCommandKeys(editingKey!);

      setIsLoading(true);
      const action = await dispatch(
        saveTargetedShiftSalaryRow({
          employmentId: props.employmentId,
          version: spec.version,
          keys: cmdKeys,
          salary: updatedSalary,
        }),
      );
      setIsLoading(false);

      if (isFulfilled(action)) {
        const newVersion = action.payload[0]?.version ?? spec.version;
        onSaved({ ...spec, version: newVersion, salaries: updatedSalaries });
        methods.reset();
        setSelectedRangeDate(null);
        onClose();
      }
    },
    [
      buildCommandKeys,
      buildOptimisticKeys,
      conflictingRow,
      dispatch,
      editingKey,
      isAddMode,
      isWithinBounds,
      methods,
      onClose,
      onSaved,
      props,
      spec,
    ],
  );

  const handleDeleteRange = useCallback(
    async (date: string) => {
      if (!editingKey || !existingSalary) return;
      const updated = removeSalaryRange(existingSalary, date);

      if (props.mode === "local") {
        if (updated.ranges.length === 0) {
          const updatedSalaries = spec.salaries.filter(
            (r) => !isDuplicateKeys(r.keys, editingKey),
          );
          onSaved({ ...spec, salaries: updatedSalaries });
          onClose();
        } else {
          const updatedSalaries = spec.salaries.map((r) =>
            isDuplicateKeys(r.keys, editingKey)
              ? { keys: editingKey, salary: updated }
              : r,
          );
          onSaved({ ...spec, salaries: updatedSalaries });
          if (selectedRangeDate === date) {
            setSelectedRangeDate(null);
            methods.setValue("validFrom", null);
            methods.setValue("components", {});
            methods.setValue("zonedComponents", {});
          }
        }
        return;
      }

      setIsLoading(true);

      if (updated.ranges.length === 0) {
        const action = await dispatch(
          removeTargetedShiftSalaryRow({
            employmentId: props.employmentId,
            version: spec.version,
            keys: toCommandKeys(editingKey),
          }),
        );
        setIsLoading(false);
        if (isFulfilled(action)) {
          const newVersion = action.payload[0]?.version ?? spec.version;
          const updatedSalaries = spec.salaries.filter(
            (r) => !isDuplicateKeys(r.keys, editingKey),
          );
          onSaved({ ...spec, version: newVersion, salaries: updatedSalaries });
          onClose();
        }
      } else {
        const action = await dispatch(
          saveTargetedShiftSalaryRow({
            employmentId: props.employmentId,
            version: spec.version,
            keys: toCommandKeys(editingKey),
            salary: updated,
          }),
        );
        setIsLoading(false);
        if (isFulfilled(action)) {
          const newVersion = action.payload[0]?.version ?? spec.version;
          const updatedSalaries = spec.salaries.map((r) =>
            isDuplicateKeys(r.keys, editingKey)
              ? { keys: editingKey, salary: updated }
              : r,
          );
          onSaved({ ...spec, version: newVersion, salaries: updatedSalaries });
          if (selectedRangeDate === date) {
            setSelectedRangeDate(null);
            methods.setValue("validFrom", null);
            methods.setValue("components", {});
            methods.setValue("zonedComponents", {});
          }
        }
      }
    },
    [
      dispatch,
      editingKey,
      existingSalary,
      methods,
      onClose,
      onSaved,
      props,
      selectedRangeDate,
      spec,
    ],
  );

  const canDeleteRange = (date: string): boolean => {
    if (!existingSalary) return false;
    return parseISO(date) > new Date();
  };

  const saveDisabled =
    isLoading ||
    !methods.formState.isValid ||
    (isAddMode &&
      (!watchedValidFrom ||
        !validateDateInput(watchedValidFrom) ||
        !isWithinBounds(watchedValidFrom))) ||
    isEmpty(methods.formState.dirtyFields) ||
    !!conflictingRow;

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      sx={{ maxWidth: "700px" }}
      dragHandleSelector="salary-edit-dialog"
    >
      <FormProvider {...methods}>
        <DialogContentWrapper
          title={t("talentProfile:targetedShiftSalaryDialogTitle")}
          margin
          onSave={methods.handleSubmit(handleSaveRange)}
          onCancel={onClose}
          saveDisabled={saveDisabled}
          isLoading={isLoading}
        >
          {/* Customer org */}
          <Grid item xs={4} alignSelf="center" textAlign="end">
            <Typography variant="secondary">
              {t("talentProfile:organization")}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            {isAddMode ? (
              <Controller
                name="custOrg"
                control={methods.control}
                render={({ field: { value, onChange } }) => (
                  <CustomerOrgCompanyAutocompleteSingle
                    value={value}
                    options={customerOptions}
                    isLoading={isCustomerLoading}
                    onInputChange={onCustomerInputChange}
                    useFixedOptions={isFixedOptions}
                    onChange={(org) => {
                      onChange(org ? { id: org.id, name: org.name } : null);
                      methods.setValue("workLocs", []);
                      const company = customerOptions.find((c) =>
                        c.customerOrganizations.some((o) => o.id === org?.id),
                      );
                      // Falling back to `null` here (instead of the fixed
                      // context company) when no org is selected/found would
                      // silently clear custCompanyId, resurrecting the
                      // empty-custCompanyId bug this dialog is scoped to.
                      const resolvedCompanyId =
                        company?.id ?? customerCompany?.id ?? null;
                      methods.setValue("custCompanyId", resolvedCompanyId);
                    }}
                  />
                )}
              />
            ) : (
              <Typography>
                {editingKey?.custOrgs[0]
                  ? editingKey.custOrgs[0].name || editingKey.custOrgs[0].id
                  : t("talentProfile:allOrganizations")}
              </Typography>
            )}
          </Grid>

          {/* Work locations */}
          <Grid item xs={4} alignSelf="center" textAlign="end">
            <Typography variant="secondary">
              {t("common:workLocation")}
            </Typography>
          </Grid>
          <Grid item xs={8}>
            {isAddMode ? (
              <WorkLocationAutocompleteFieldMulti
                name="workLocs"
                customerOrgId={watchedCustOrg?.id ?? undefined}
                contractId={contractId}
                useFixedOptions={true}
                groupByCustomerOrg={false}
                disabled={!watchedCustOrg}
                placeholder={t("talentProfile:allWorkLocations")}
                onChangeOverride={(wls) =>
                  methods.setValue(
                    "workLocs",
                    wls?.map((wl) => ({ id: wl.id, name: wl.name })) ?? [],
                    { shouldDirty: true },
                  )
                }
              />
            ) : (
              <Typography>
                {editingKey?.workLocs.length
                  ? editingKey.workLocs.map((wl) => wl.name || wl.id).join(", ")
                  : t("talentProfile:allWorkLocations")}
              </Typography>
            )}
          </Grid>

          {/* Work titles */}
          <Grid item xs={4} alignSelf="center" textAlign="end">
            <Typography variant="secondary">{t("common:workTitle")}</Typography>
          </Grid>
          <Grid item xs={8}>
            {isAddMode ? (
              <WorkTitleAutocompleteField
                name="workTitles"
                multiple={true}
                fetchAll={false}
                placeholder={t("common:workTitle")}
              />
            ) : (
              <Typography>
                {editingKey?.workTitles.length
                  ? editingKey.workTitles
                      .map((id) => workTitleTranslations?.[id] ?? id)
                      .join(", ")
                  : t("common:workTitle")}
              </Typography>
            )}
          </Grid>

          {/* TES info */}
          <Grid item xs={4} alignSelf="center" textAlign="end">
            <Typography variant="secondary">{t("common:tes")}</Typography>
          </Grid>
          <Grid item xs={8}>
            <Typography>{tesTranslations?.[tesId] ?? tesId}</Typography>
          </Grid>

          {isAddMode && conflictingRow && (
            <>
              <Grid item xs={4} alignSelf="center" textAlign="end" />
              <Grid item xs={8}>
                <InputHelperText
                  errorMessage={t(
                    "talentProfile:targetedShiftSalaryOverlappingKeys",
                  )}
                  TypographyProps={{ ml: 0 }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Date + salary components — add mode, or when a history row is selected in edit mode. Hidden while the
              current selection conflicts with another rule, since there is nothing sensible to save yet. */}
          {(isAddMode || selectedRangeDate || !existingSalary) &&
            !conflictingRow && (
              <>
                <Grid item xs={4} alignSelf="center" textAlign="end">
                  <Typography variant="secondary">
                    {t("talentProfile:validFrom")}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Controller
                    name="validFrom"
                    control={methods.control}
                    rules={
                      isAddMode
                        ? {
                            required: true,
                            validate: (d) => {
                              if (d == null) return false;
                              if (!validateDateInput(d as Date)) return false;
                              if (!isWithinBounds(d as Date)) {
                                return t(
                                  "talentProfile:targetedShiftSalaryDateOutOfRange",
                                ) as string;
                              }
                              return true;
                            },
                          }
                        : {}
                    }
                    render={({ field }) => (
                      <DesktopDatePicker
                        readOnly={!isAddMode}
                        disabled={!isAddMode}
                        value={field.value}
                        format={leadingZerosDateFormat}
                        minDate={minDate ?? undefined}
                        maxDate={maxDate ?? undefined}
                        onChange={(d: Date | null) => field.onChange(d)}
                        slots={{ textField: CommonTextField }}
                        slotProps={{
                          textField: {
                            error: !!methods.formState.errors?.validFrom,
                            size: "small",
                            sx: {
                              input: { textAlign: "center" },
                              width: "135px",
                            },
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} />
                <Grid item xs={8}>
                  <InputHelperText
                    errorMessage={
                      methods.formState.errors?.validFrom?.message as string
                    }
                  />
                </Grid>
                <SalaryComponents
                  tesId={tesId}
                  validFrom={watchedValidFrom as Date | null}
                  salaryZones={salaryZones}
                  defaultValues={
                    selectedRangeDate
                      ? existingSalary?.ranges.find(
                          (r) => r.from === selectedRangeDate,
                        )?.components
                      : undefined
                  }
                />
              </>
            )}

          {/* Salary history table — edit mode only */}
          {!isAddMode && existingSalary && (
            <>
              {selectedRangeDate && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormSectionHeader
                  variant="header"
                  fontSize="14px"
                  sx={{ mb: 1 }}
                >
                  {t("talentProfile:salarySettings")}
                </FormSectionHeader>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("talentProfile:validFrom")}</TableCell>
                      <TableCell>{t("talentProfile:hourlyWage")}</TableCell>
                      <TableCell>{t("talentProfile:totalSalary")}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...existingSalary.ranges]
                      .sort((a, b) => b.from.localeCompare(a.from))
                      .map((range) => {
                        const isSelected = selectedRangeDate === range.from;
                        const total = (
                          <ZonedSalaryValue
                            defaultValue={calculateTotalSalary(
                              range.components ?? {},
                            )}
                            zones={salaryZones}
                            zoneValues={calculateZonedTotalSalary(
                              range.zonedComponents,
                            )}
                          />
                        );
                        return (
                          <TableRow
                            key={range.from}
                            selected={isSelected}
                            sx={{
                              cursor: "pointer",
                              "&.Mui-selected": {
                                backgroundColor: (theme) =>
                                  theme.palette.background.default,
                                "&:hover": {
                                  backgroundColor: (theme) =>
                                    alpha(
                                      theme.palette.background.default,
                                      0.8,
                                    ),
                                },
                              },
                            }}
                            onClick={() =>
                              setSelectedRangeDate(
                                isSelected ? null : range.from,
                              )
                            }
                          >
                            <TableCell>
                              <Typography
                                fontSize="16px"
                                lineHeight="19px"
                                fontWeight={isSelected ? "600" : "normal"}
                              >
                                {format(
                                  parseISO(range.from),
                                  leadingZerosDateFormat,
                                )}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography fontSize="16px" lineHeight="19px">
                                {total}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                fontSize="16px"
                                lineHeight="19px"
                                fontWeight="600"
                              >
                                {total}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {canDeleteRange(range.from) && canEdit && (
                                <Tooltip title={t("common:delete")}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteRange(range.from);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </Grid>
            </>
          )}
          <Grid item>
            <TargetedShiftInfoText sx={{ padding: "16px 32px 0px 48px" }}>
              {t("talentProfile:targetedShiftSalaryDialogInfoText")}
            </TargetedShiftInfoText>
          </Grid>
        </DialogContentWrapper>
      </FormProvider>
    </DialogComponent>
  );
};

export default TargetedShiftSalaryDialog;
